import cloneDeep from 'lodash/cloneDeep'
import { objectToMap, deleteManyFieldsInMapAndGetNewMap } from '@/helpers/map'
import { deepDelete } from '@/helpers/deepDelete'
import { assertArray } from '@/config/helpers.js'
import { DateTime } from 'luxon'
import { mapGetters } from 'vuex'

export const campaignHelpers = {
  computed: {
    ...mapGetters(['units']),
  },
  methods: {
    formatCampaign(offerSchedule) {
      return {
        ...offerSchedule,
        start_time: offerSchedule?.start_time ? offerSchedule.start_time.slice(0, 5) : '00:00',
        end_time: offerSchedule?.end_time ? offerSchedule.end_time.slice(0, 5) : '00:00',
        offer_sales: assertArray(offerSchedule?.offer_sales).length
          ? this.formatOfferSalesInGroupsAndSingleProducts(offerSchedule.offer_sales, 'offer_sales')
          : [],
        percentage_offer_sales: assertArray(offerSchedule?.percentage_offer_sales).length
          ? this.formatOfferSalesInGroupsAndSingleProducts(offerSchedule.percentage_offer_sales, 'percentage_offer_sales')
          : [],
        dynamic_pricing_percentage_offer_sales: assertArray(offerSchedule?.dynamic_pricing_percentage_offer_sales).length
          ? this.formatOfferSalesInGroupsAndSingleProducts(offerSchedule.dynamic_pricing_percentage_offer_sales, 'dynamic_pricing_percentage_offer_sales')
          : [],
        takepay_offer_sales: assertArray(offerSchedule?.takepay_offer_sales).length
          ? this.formatOfferSalesInGroupsAndSingleProducts(offerSchedule.takepay_offer_sales, 'takepay_offer_sales')
          : [],
        combo_offer_sales: assertArray(offerSchedule?.combo_offer_sales).length
          ? offerSchedule.combo_offer_sales.map((item) => {
              item.trigger_products = this.formatOfferSalesInGroupsAndSingleProducts(item.trigger_products, 'trigger_products')
              item.target_products = this.formatOfferSalesInGroupsAndSingleProducts(item.target_products, 'target_products')

              return item
            })
          : [],
        cashback_percentage_offer_sales: assertArray(offerSchedule?.cashback_percentage_offer_sales).length
          ? this.formatOfferSalesInGroupsAndSingleProducts(offerSchedule.cashback_percentage_offer_sales, 'cashback_percentage_offer_sales')
          : [],
        cashback_absolute_offer_sales: assertArray(offerSchedule?.cashback_absolute_offer_sales).length
          ? this.formatOfferSalesInGroupsAndSingleProducts(offerSchedule.cashback_absolute_offer_sales, 'cashback_absolute_offer_sales')
          : [],
        points_offer_sales: assertArray(offerSchedule?.points_offer_sales).length
          ? this.formatOfferSalesInGroupsAndSingleProducts(offerSchedule.points_offer_sales, 'points_offer_sales')
          : [],
        next_buy_cashback_offer_sales: assertArray(offerSchedule?.next_buy_cashback_offer_sales).length
          ? this.formatOfferSalesInGroupsAndSingleProducts(offerSchedule.next_buy_cashback_offer_sales, 'next_buy_cashback_offer_sales')
          : [],
        dynamic_rule_offer_sales: offerSchedule?.dynamic_rule_offer_sales || [],
        cross_sell_offer_sales: offerSchedule?.cross_sell_offer_sales || [],
        units: offerSchedule.units.map((unit) => unit.id_erp),
        units_configuration: offerSchedule.units.map((unit) => {
          return { value: unit.id_erp, dataType: unit.dataType }
        }),
        partner_units: offerSchedule.units
          .map((unit) => unit.ecommerce_partner?.partner_unit_id)
          .filter((partner_unit_id) => partner_unit_id !== undefined && partner_unit_id !== null),
        auto_distributed: offerSchedule?.auto_distributed || false,
        user_distribution_models: offerSchedule?.user_distribution_models || [],
      }
    },

    formatOfferSalesInGroupsAndSingleProducts(offerSales, offerSaleType) {
      const offerSalesFormatted = offerSales.reduce((offerSalesFormatted, offer) => {
        if (offer?.[offerSaleType]) {
          const { [offerSaleType]: groupProducts, ...group } = offer
          const newGroup = {
            origin: 'search',
            original_eans: offer.eans || [],
            imported: false,
            isGroup: true,
            products: groupProducts.map((product) => {
              if (product.sync_data) {
                deepDelete(product.sync_data, ['__typename'])
              }
              return {
                active: product.active || true,
                original_eans: product.eans,
                distribution_limit: product.distribution_limit || null,
                ...product,
              }
            }),
          }

          offerSalesFormatted.push(Object.assign(newGroup, group))
        } else {
          const eans = offer.eans || []

          if (offer.product && offer.product.eans) {
            const filteredEans = eans.filter((ean) => !offer.product.eans.includes(ean))
            offer._id = offer.product._id

            if (filteredEans.length) {
              offer.product.eans.push(...filteredEans)
            }
          }

          offerSalesFormatted.push({
            ...offer,
            distribution_limit: offer.distribution_limit || null,
          })
        }
        return offerSalesFormatted
      }, [])

      return offerSalesFormatted
    },

    formatCampaignToSave(isDraft) {
      const campaignClone = cloneDeep(this.campanha)
      let campaign = objectToMap(campaignClone)

      if (campaign.get('segments')) {
        campaign.set(
          'segments',
          campaign.get('segments').map((segment) => {
            return segment._id
          })
        )
      }

      campaign.set(
        'offer_sales',
        this.formatOfferSalesFields(
          campaign.get('offer_sales'),
          {
            maximum_per_client: 0,
            minimum_per_client: 0,
          },
          'offer_sales'
        )
      )

      campaign.set(
        'percentage_offer_sales',
        this.formatOfferSalesFields(
          campaign.get('percentage_offer_sales'),
          {
            percentage_discount: 0,
            maximum_per_client: 0,
            minimum_per_client: 0,
          },
          ['price_1', 'price_2'],
          'percentage_offer_sales'
        )
      )

      campaign.set(
        'dynamic_pricing_percentage_offer_sales',
        this.formatOfferSalesFields(
          campaign.get('dynamic_pricing_percentage_offer_sales'),
          {
            percentage_discount: 0,
            maximum_per_client: 0,
            minimum_per_client: 0,
          },
          ['price_1', 'price_2'],
          'dynamic_pricing_percentage_offer_sales'
        )
      )

      campaign.set(
        'cashback_percentage_offer_sales',
        this.formatOfferSalesFields(
          campaign.get('cashback_percentage_offer_sales'),
          {
            cashback_percentage: 0,
            maximum_per_client: 0,
            minimum_per_client: 0,
          },
          ['price_2'],
          'cashback_percentage_offer_sales'
        )
      )

      campaign.set(
        'cashback_absolute_offer_sales',
        this.formatOfferSalesFields(
          campaign.get('cashback_absolute_offer_sales'),
          {
            cashback_absolute: 0,
            maximum_per_client: 0,
            minimum_per_client: 0,
          },
          ['cashback_percentage', 'price_1', 'price_2'],
          'cashback_absolute_offer_sales'
        )
      )

      campaign.set(
        'points_offer_sales',
        this.formatOfferSalesFields(
          campaign.get('points_offer_sales'),
          {
            additional_points_rate: 0,
            maximum_per_client: 0,
            minimum_per_client: 0,
          },
          ['cashback_percentage', 'price_1', 'price_2'],
          'points_offer_sales'
        )
      )

      campaign.set(
        'next_buy_cashback_offer_sales',
        this.formatOfferSalesFields(
          campaign.get('next_buy_cashback_offer_sales'),
          {
            cashback_absolute_discount: 0,
            max_coupons: 0,
            minimum_per_client: 0,
          },
          ['cashback_percentage', 'price_1', 'price_2'],
          'next_buy_cashback_offer_sales'
        )
      )

      campaign.set(
        'takepay_offer_sales',
        this.formatOfferSalesFields(
          campaign.get('takepay_offer_sales'),
          {
            take: 0,
            pay: 0,
            maximum_per_client: 0,
          },
          ['price_1', 'price_2', 'additional_points_rate', 'cashback', 'minimum_per_client'],
          'takepay_offer_sales'
        )
      )

      if (campaign.get('next_buy_cashback_offer_sales') && campaign.get('next_buy_cashback_offer_sales').length) {
        campaign.get('next_buy_cashback_offer_sales').forEach((item) => {
          item.cashback_expiration_date = this.getFormatedCashbackExpirationDate(item.cashback_expiration_date)
        })
      }

      if (campaign.get('combo_offer_sales') && campaign.get('combo_offer_sales').length) {
        campaign.get('combo_offer_sales').forEach((item) => {
          delete item.__typename
          if (item.combo_sync_data) {
            deepDelete(item.combo_sync_data, ['__typename'])
            if (item.combo_sync_data.sync_data) {
              deepDelete(item.combo_sync_data.sync_data, ['__typename'])
            }
          }
          item.maximum_per_client =
            typeof item.maximum_per_client === 'string' && item.maximum_per_client.length
              ? parseInt(item.maximum_per_client)
              : typeof item.maximum_per_client === 'number'
              ? item.maximum_per_client
              : 0
          item.trigger_products = this.formatOfferSalesFields(
            item.trigger_products,
            {
              quantity: 0,
            },
            ['active', 'image', 'categories_final_consumer', 'image_url', 'rules_exhibition', 'percentage_discount', 'distribution_limit']
          )

          item.target_products = this.formatOfferSalesFields(
            item.target_products,
            {
              percentage_discount: 0,
            },
            ['active', 'image', 'categories_final_consumer', 'image_url', 'rules_exhibition', 'quantity', 'distribution_limit']
          ).map((item) => ({ ...item, percentage_discount: Number(item.percentage_discount) }))
        })
        campaign.set('combo_offer_sales', campaign.get('combo_offer_sales'))
      }

      if (campaign.get('dynamic_rule_offer_sales') && campaign.get('dynamic_rule_offer_sales').length) {
        campaign.get('dynamic_rule_offer_sales').forEach((item) => {
          delete item.__typename
          delete item.trigger_rules.__typename
          delete item.benefits.__typename
          delete item.target_rules.__typename
          item.trigger_products = this.formatOfferSalesFields(
            item.trigger_products,
            {
              equivalent_quantity: 0,
            },
            ['trigger_amount']
          )
        })
        campaign.set('dynamic_rule_offer_sales', campaign.get('dynamic_rule_offer_sales'))
      }

      if (campaign.get('cross_sell_offer_sales') && campaign.get('cross_sell_offer_sales').length) {
        campaign.get('cross_sell_offer_sales').forEach((item) => {
          delete item.__typename
          delete item.trigger_rules.__typename
          delete item.target_rules.__typename
          item.trigger_products = this.formatOfferSalesFields(item.trigger_products, {}, ['trigger_amount'])
          item.target_products = this.formatOfferSalesFields(item.target_products, {}, ['discount'])
        })
        campaign.set('cross_sell_offer_sales', campaign.get('cross_sell_offer_sales'))
      }

      const units = this.getFormatedUnits() || []
      campaign.set('units', units)

      const partner_units = this.units
        .filter((unit) => units.includes(unit.id_erp))
        .map((unit) => unit.ecommerce_partner?.partner_unit_id)
        .filter((partner_unit_id) => partner_unit_id !== undefined && partner_unit_id !== null)
      campaign.set('partner_units', partner_units)

      campaign.set('active', !isDraft)

      if (this.isIntelligentDistributionEnabled) {
        campaign.set('auto_distributed', true)

        const distributionModel = this.getDistributionModelForAPI()
        if (distributionModel) {
          campaign.set('user_distribution_models', [distributionModel])
        }

        campaign.set('start_date', this.getFormatedStartDateForIntelligentDistribution())
        campaign.set('end_date', this.getFormatedEndDateForIntelligentDistribution())

        campaign.delete('flash')
        campaign.delete('start_time')
        campaign.delete('end_time')
        campaign.delete('pdv_margin_time')

        campaign.set('cashback_absolute_offer_sales', [])
        campaign.set('cashback_percentage_offer_sales', [])
        campaign.set('points_offer_sales', [])
      } else {
        campaign.set('auto_distributed', false)
        campaign.set('user_distribution_models', [])

        campaign.set('start_date', this.getFormatedStartDate())
        campaign.set('end_date', this.getFormatedEndDate())
      }

      if (campaign.get('start_date') === '') campaign.delete('start_date')
      if (campaign.get('end_date') === '') campaign.delete('end_date')

      if (campaign.get('industry')) {
        deepDelete(campaign.get('industry'), ['__typename'])
        deepDelete(campaign.get('industry'), ['accept_history'])
        deepDelete(campaign.get('industry'), ['flyer_url'])
        const industry = campaign.get('industry')
        industry.proposal_deadline = this.getFormatedProposalDeadline(industry.proposal_deadline)
        campaign.set('industry', industry)
      }

      if (campaign.get('rules')) {
        deepDelete(campaign.get('rules'), ['__typename'])
        campaign.set('rules', campaign.get('rules'))
      }

      if (campaign.get('channel_configuration')) {
        deepDelete(campaign.get('channel_configuration'), ['__typename'])
        campaign.set('channel_configuration', campaign.get('channel_configuration'))
      }

      campaign = deleteManyFieldsInMapAndGetNewMap(campaign, [
        'auto',
        'status',
        'eans',
        'draft',
        'products_count',
        'finalized',
        'created_at',
        'updated_at',
        'products',
        'has_results',
        'has_segment_data',
        'types',
        'communication',
        'campaigns',
        '__typename',
        'start_time',
        'end_time',
        'model',
        'offers_per_consumer',
      ])

      return Object.fromEntries(campaign)
    },

    formatOfferSalesFields(offerSales, extraFieldsDefaultValues, customParamsToRemove, type) {
      return assertArray(offerSales).reduce((offerSalesFormatted, offer) => {
        let productOrGroup = objectToMap(offer)
        const productOrGroupId = productOrGroup.get('_id')
        const paramsToRemove = [
          'ean',
          'imported',
          'origin',
          'repeated',
          'isPersisted',
          'product',
          'original_eans',
          '__typename',
          'disabled',
          'isGroup',
          'index',
          '_id',
          'units_with_price_1',
          'franchisee_eans_frequency_percentage',
        ].concat(customParamsToRemove)

        productOrGroup = deleteManyFieldsInMapAndGetNewMap(productOrGroup, paramsToRemove)

        if (productOrGroup.has('sync_data')) {
          deepDelete(productOrGroup.get('sync_data'), ['__typename'])
        }

        if (['cashback_absolute_offer_sales', 'cashback_percentage_offer_sales'].includes(type)) {
          productOrGroup.set('expiration_cashback', this.cashbackExpiration)
        }

        if (productOrGroup.has('products')) {
          const productsGroups = productOrGroup.get('products').map((productGroup) => {
            let newProductGroup = objectToMap(productGroup)
            const paramsProductToRemove = [
              'ean',
              'imported',
              'origin',
              'repeated',
              'disabled',
              'isPersisted',
              'product',
              'finalized',
              'original_eans',
              '__typename',
              'isGroup',
              'index',
              '_id',
              'nameGroup',
              'groupInfo',
              'units_with_price_1',
              'franchisee_eans_frequency_percentage',
            ].concat(customParamsToRemove)
            newProductGroup = deleteManyFieldsInMapAndGetNewMap(newProductGroup, paramsProductToRemove)

            if (newProductGroup.has('sync_data')) {
              deepDelete(newProductGroup.get('sync_data'), ['__typename'])
            }

            const groupName = productOrGroup.get('name')
            let currentProductWithouProducts = new Map(productOrGroup)
            currentProductWithouProducts = deleteManyFieldsInMapAndGetNewMap(currentProductWithouProducts, ['products', 'name', 'eans'])

            newProductGroup.set('group_name', groupName)
            newProductGroup.set('group_id', productOrGroupId)

            if (extraFieldsDefaultValues) {
              for (const [fieldName, defaultValue] of Object.entries(extraFieldsDefaultValues)) {
                currentProductWithouProducts.set(fieldName, productOrGroup.get(fieldName) || defaultValue)
              }
            }

            if (this.isIntelligentDistributionEnabled && newProductGroup.has('distribution_limit') && !['trigger_products', 'target_products'].includes(type)) {
              newProductGroup.set('distribution_limit', newProductGroup.get('distribution_limit'))
            }

            const productMerge = Object.assign(Object.fromEntries(newProductGroup), Object.fromEntries(currentProductWithouProducts))

            const group_categories_final_consumer = {
              department_id: productMerge?.categories_final_consumer?.department?._id,
              category_id: productMerge?.categories_final_consumer?.category?._id,
              sub_category_id: productMerge?.categories_final_consumer?.sub_category?._id,
            }
            deepDelete(productMerge, ['__typename', 'categories_final_consumer'])
            if (this.isNewOffer && group_categories_final_consumer?.department_id) {
              productMerge.group_categories_final_consumer = group_categories_final_consumer
            }
            return productMerge
          })

          offerSalesFormatted.push(...productsGroups.reverse())
        } else {
          productOrGroup.set('eans', productOrGroup.get('eans').eans || productOrGroup.get('eans'))

          if (extraFieldsDefaultValues) {
            for (const [fieldName, defaultValue] of Object.entries(extraFieldsDefaultValues)) {
              productOrGroup.set(fieldName, productOrGroup.get(fieldName) || defaultValue)
            }
          }

          if (this.isIntelligentDistributionEnabled && productOrGroup.has('distribution_limit') && !['trigger_products', 'target_products'].includes(type)) {
            productOrGroup.set('distribution_limit', productOrGroup.get('distribution_limit'))
          }

          offerSalesFormatted.push(Object.fromEntries(productOrGroup))
        }

        return offerSalesFormatted
      }, [])
    },

    getFormatedUnits() {
      if (this.campanha.units && this.campanha.units.length) return this.campanha.units.map((unit) => String(unit))
    },
    getFormatedCashbackExpirationDate(date) {
      if (!date) return null
      return DateTime.fromISO(date).endOf('day').toFormat('yyyy-MM-dd HH:mm:ss')
    },
    getFormatedStartDate() {
      if (!this.campanha.start_date) return null
      if (this.campanha.start_time) return `${this.campanha.start_date} ${this.campanha.start_time}:00`
      return DateTime.fromISO(this.campanha.start_date).startOf('day').toFormat('yyyy-MM-dd HH:mm:ss')
    },
    getFormatedEndDate() {
      if (!this.campanha.end_date) return null
      if (this.campanha.end_time) return `${this.campanha.end_date} ${this.campanha.end_time}:59`
      return DateTime.fromISO(this.campanha.end_date).endOf('day').toFormat('yyyy-MM-dd HH:mm:ss')
    },
    getFormatedProposalDeadline() {
      if (!this.campanha.industry.proposal_deadline) return null
      return DateTime.fromISO(this.campanha.industry.proposal_deadline).endOf('day').toFormat('yyyy-MM-dd HH:mm:ss')
    },

    getFormatedStartDateForIntelligentDistribution() {
      if (!this.campanha.start_date) return ''
      return DateTime.fromISO(this.campanha.start_date).startOf('day').toFormat('yyyy-MM-dd HH:mm:ss')
    },

    getFormatedEndDateForIntelligentDistribution() {
      if (!this.campanha.end_date) return ''
      return DateTime.fromISO(this.campanha.end_date).endOf('day').toFormat('yyyy-MM-dd HH:mm:ss')
    },
  },
}
